import { plain } from '../../lib/labels';
import { useState } from 'react';
import { Card, Table, Tag, Typography, Space, Button, Modal, Input, App, Popconfirm, type TableColumnsType } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { QueryError } from '../../lib/QueryState';
import { Can } from '../../rbac/RequirePermission';
import { ListenerName, listenerText } from '../../lib/listenerName';
import { LoadMore, pageFromOverfetch, useCursorPages } from '../../lib/useCursorPages';

/** Rows per request; the endpoint returns one extra row when there are more. */
const PAGE = 50;

interface KycRow {
  id: string;
  type: 'pan' | 'aadhaar' | 'licence' | 'other';
  status: 'uploaded' | 'verified' | 'rejected';
  last4: string | null;
  rejectionReason: string | null;
  createdAt: string;
  listener: { id: string; displayName?: string | null; user: { id: string; handle: string } };
}

export function KycQueuePage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [rejecting, setRejecting] = useState<KycRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [q, setQ] = useState('');
  const list = useCursorPages<KycRow>(['admin', 'kyc', 'queue', q], async (cursor) =>
    pageFromOverfetch(
      (
        await api.get<KycRow[]>('/admin/kyc/queue', {
          params: { status: 'uploaded', limit: PAGE, cursor, q: q || undefined },
        })
      ).data,
      PAGE,
    ),
  );

  const verify = useMutation({
    mutationFn: (id: string) => api.post(`/admin/kyc/${id}/verify`),
    onSuccess: () => {
      message.success('Verified');
      qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
      qc.invalidateQueries({ queryKey: ['admin', 'kyc', 'queue'] });
    },
    onError: () => message.error('Could not verify'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/kyc/${id}/reject`, { reason }),
    onSuccess: () => {
      message.success('Rejected');
      qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
      qc.invalidateQueries({ queryKey: ['admin', 'kyc', 'queue'] });
      setRejecting(null);
      setRejectReason('');
    },
    onError: () => message.error('Could not reject'),
  });

  async function openView(row: KycRow) {
    try {
      const res = await api.get<{ url: string; isOriginal: boolean }>(`/admin/kyc/${row.id}/view-url`, {
        params: { original: row.type === 'aadhaar' ? false : true },
      });
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch {
      message.error('Could not get view URL');
    }
  }

  const columns: TableColumnsType<KycRow> = [
    {
      title: 'Listener',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2}>
          <ListenerName
            listener={{ displayName: row.listener.displayName, handle: row.listener.user.handle, id: row.listener.id }}
            listenerId={row.listener.id}
            tab="application"
          />
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (t: KycRow['type']) => <Tag color={t === 'aadhaar' ? 'magenta' : t === 'pan' ? 'gold' : 'blue'}>{t.toUpperCase()}</Tag>,
    },
    { title: 'Last 4', dataIndex: 'last4', render: (v: string | null) => v ?? '—' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: KycRow['status']) => (
        <Tag color={s === 'verified' ? 'success' : s === 'rejected' ? 'error' : 'processing'}>{plain(s)}</Tag>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleString(),
    },
    {
      title: 'Actions',
      render: (_: unknown, row) => (
        <Space>
          <Can permission="kyc.document.view">
            <Button size="small" onClick={() => openView(row)}>
              View
            </Button>
          </Can>
          <Can permission="kyc.decide">
            <Popconfirm
              title="Mark this document verified?"
              description="Only after you have opened and checked it."
              okText="Verify"
              onConfirm={() => verify.mutate(row.id)}
            >
              <Button size="small" type="primary" loading={verify.isPending && verify.variables === row.id}>
                Verify
              </Button>
            </Popconfirm>
          </Can>
          <Can permission="kyc.decide">
            <Button size="small" danger onClick={() => setRejecting(row)}>
              Reject
            </Button>
          </Can>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        ID verification
      </Typography.Title>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            aria-label="Search KYC"
            placeholder="Listener name or @handle"
            onSearch={(v) => setQ(v.trim())}
            style={{ minWidth: 280 }}
          />
        </Space>
        {list.isError && <QueryError error={list.error} what="ID documents" onRetry={list.refetch} style={{ marginBottom: 12 }} />}
        <Table
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.rows}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          locale={{ emptyText: q ? `No documents waiting for “${q}”` : 'No documents waiting for review' }}
        />
        <LoadMore list={list} noun="documents" />
      </Card>

      <Modal
        title={rejecting ? `Reject ${rejecting.type.toUpperCase()} for ${listenerText({ displayName: rejecting.listener.displayName, handle: rejecting.listener.user.handle })}` : ''}
        open={!!rejecting}
        onCancel={() => setRejecting(null)}
        onOk={() => rejecting && reject.mutate({ id: rejecting.id, reason: rejectReason })}
        okButtonProps={{ danger: true, disabled: rejectReason.trim().length < 10, loading: reject.isPending }}
        okText="Reject"
      >
        <Typography.Paragraph type="secondary">
          The reason will be shown to the listener. Be specific (≥ 10 chars).
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. PAN photo is blurred; please re-upload with clear lighting."
        />
      </Modal>
    </Space>
  );
}
